import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Box, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Accept, useDropzone } from 'react-dropzone';

import { StyledErrorMessage } from '@/shared/components';
import { handleFileRejections, setAlert, validateFileType } from '@/shared/components/dropzone/duck';
import { globalTypes } from '@/shared/duck';
import { useMedia } from '@/shared/hooks';

import { InitialFilesBlock } from './components';

interface DropzoneProps {
  onDrop: (files: File[]) => void;
  accept?: Accept;
  maxFiles?: number;
  maxSize?: number; // bytes
  multiple?: boolean;
  formName?: string;
  formError?: string;
  disabled?: boolean;
  initialFiles?: globalTypes.InitialDropzoneFile[];
  onDeleteInitialFile?: (fileId: string) => void;
  onDownloadInitialFile?: (filePath: string) => void;
}

interface DropzoneRef {
  reset: () => void;
}

const UIDropzone= React.forwardRef<DropzoneRef, DropzoneProps>(({
  onDrop,
  accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
  },
  maxFiles = 5,
  maxSize = 10485760, // 10 MB
  multiple = false,
  formName,
  formError,
  disabled = false,
  initialFiles = [],
  onDeleteInitialFile,
  onDownloadInitialFile
}, ref) => {
  const { isSxScreen } = useMedia();
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const [fileIds, setFileIds] = React.useState<Map<string, string>>(new Map());

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      setUploadedFiles([]);
      setFileIds(new Map());
      onDrop([]);
    },
  }));

  const remainingFilesCount = React.useMemo(() => {
    return Math.max(0, maxFiles - (uploadedFiles.length + initialFiles.length));
  }, [maxFiles, uploadedFiles.length, initialFiles.length]);

  const handleUpload = React.useCallback((acceptedFiles: File[], fileRejections: any[]) => {

    const totalFilesAfterUpload = uploadedFiles.length + initialFiles.length + acceptedFiles.length;
    if (totalFilesAfterUpload > maxFiles) {
      setAlert(
        enqueueSnackbar,
        `You can only upload a maximum of ${maxFiles} files. You already have ${uploadedFiles.length + initialFiles.length} files.`,
        'error'
      );
      return;
    }

    handleFileRejections(enqueueSnackbar, fileRejections, maxSize, maxFiles);

    const newFileIds = new Map(fileIds);

    for (const file of acceptedFiles) {
      newFileIds.set(file.name, crypto.randomUUID());
    }

    let updatedFiles = [...uploadedFiles];
    for (const newFile of acceptedFiles) {
      const existingIndex = updatedFiles.findIndex(
        uploaded => uploaded.name === newFile.name
      );

      if (existingIndex !== -1) {
        updatedFiles[existingIndex] = newFile;
        setAlert(enqueueSnackbar, `The file "${newFile.name}" was successfully replaced.`, 'success');
      } else {
        updatedFiles.push(newFile);
      }
    }

    if (multiple && updatedFiles.length > maxFiles) {
      updatedFiles = updatedFiles.slice(0, maxFiles);
    }

    setUploadedFiles(updatedFiles);
    setFileIds(newFileIds);
    onDrop(updatedFiles);
  }, [uploadedFiles, fileIds, enqueueSnackbar, maxSize, maxFiles, multiple, onDrop, initialFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    validator: (file: File) => {
      if (!validateFileType(file)) {
        return {
          code: 'invalid-file-type',
          message: 'Invalid file type'
        };
      }
      return null;
    },
    accept,
    multiple,
    maxFiles,
    maxSize,
    disabled: disabled || remainingFilesCount <= 0,
  });

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...uploadedFiles];
    const removedFile = updatedFiles[index];
    updatedFiles.splice(index, 1);

    const newFileIds = new Map(fileIds);
    newFileIds.delete(removedFile.name);

    setUploadedFiles(updatedFiles);
    setFileIds(newFileIds);
    onDrop(updatedFiles);
  };

  return (
    <Stack gap='1.5rem'>
      <Stack gap={0}>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: formError ? 'error.main' : 'grey.100',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: isDragActive ? 'primary.light' : 'background.paper',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: '3rem', color: 'info.main' }} />
          <Typography variant='body1' sx={{ mt: 1 }}>
            {!isSxScreen && 'Drag & Drop or '}<Typography component='span' color='info.main'>Choose file</Typography> to upload
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            Supported formats: .XLS, .XLSX
          </Typography>
        </Box>
        {formName && <StyledErrorMessage name={formName} />}
      </Stack>

      {initialFiles.length > 0 && (
        <InitialFilesBlock
          initialFiles={initialFiles}
          disabled={disabled}
          onDownloadInitialFile={onDownloadInitialFile}
          onDeleteInitialFile={onDeleteInitialFile}
        />
      )}

      {initialFiles.length > 0 && uploadedFiles.length > 0 && (
        <Divider />
      )}

      <Stack>
        {uploadedFiles.map((file, index) => (
          <Paper
            key={fileIds.get(file.name) || index}
            elevation={3}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem',
              borderRadius: '8px',
            }}
          >
            <Box display='flex' alignItems='center' gap={2} width='100%'>
              <InsertDriveFileIcon color='info' />
              <Box width='100%'>
                <Typography
                  variant='body2'
                  width='fit-content'
                  noWrap
                >
                  {file.name}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {(
                    file.size / 1048576
                  ).toFixed(2)} MB
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => handleRemoveFile(index)}
              color='error'
              disabled={disabled}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
});

export default UIDropzone;